import React, { Component, useState, useEffect, useCallback } from 'react';
import Sidebar from './components/Sidebar';
import FeedbackLauncher from './components/FeedbackLauncher';
import LoginPage from './pages/LoginPage';
import RecoveryPage from './pages/RecoveryPage';
import SecuritySetupPage from './pages/SecuritySetupPage';
import Dashboard from './pages/Dashboard';
import Patients from './pages/Patients';
import Encounters from './pages/Encounters';
import Labs from './pages/Labs';
import Medications from './pages/Medications';
import Vitals from './pages/Vitals';
import Allergies from './pages/Allergies';
import ChatPage from './pages/ChatPage';
import EmailPage from './pages/EmailPage';
import MessagesPage from './pages/MessagesPage';
import AIResearchPage from './pages/AIResearchPage';
import BIDashboard from './pages/BIDashboard';
import BillingPage from './pages/BillingPage';
import CRMPage from './pages/CRMPage';
import AdminAIPage from './pages/AdminAIPage';
import SchedulingPage from './pages/SchedulingPage';
import NotificationsPage from './pages/NotificationsPage';
import DataExportPage from './pages/DataExportPage';
import PatientPortalPage from './pages/PatientPortalPage';
import PatientPortalView from './pages/PatientPortalView';
import PublicProposalPage from './pages/PublicProposalPage';
import LeadIntakeUploadPage from './pages/LeadIntakeUploadPage';
import LeadEducationGuidePage from './pages/LeadEducationGuidePage';
import TOTPSetupPage from './pages/TOTPSetupPage';
import AuditLog from './pages/AuditLog';
import SettingsPage from './pages/SettingsPage';
import AWSCloudPage from './pages/AWSCloudPage';
import GoHighLevelPage from './pages/GoHighLevelPage';
import StripePage from './pages/StripePage';
import StripeReportsPage from './pages/StripeReportsPage';
import ResearchStudiesPage from './pages/ResearchStudiesPage';
import ResearchDatabasePage from './pages/ResearchDatabasePage';
import BillingEstimatesPage from './pages/BillingEstimatesPage';
import RevenueHubPage from './pages/RevenueHubPage';
import BiolabPage from './pages/BiolabPage';
import ReportBuilderPage from './pages/ReportBuilderPage';
import CeoDashboardPage from './pages/CeoDashboardPage';
import PersonnelManagementPage from './pages/PersonnelManagementPage';
import LeaveRequestsPage from './pages/LeaveRequestsPage';
import PersonnelActionsPage from './pages/PersonnelActionsPage';
import PersonnelHubPage from './pages/PersonnelHubPage';
import AccessControlHubPage from './pages/AccessControlHubPage';
import AIAlertsPage from './pages/AIAlertsPage';
import ActivityPage from './pages/ActivityPage';
import TelemedicinePage from './pages/TelemedicinePage';
import TelehealthPage from './pages/TelehealthPage';
import TelehealthHubPage from './pages/TelehealthHubPage';
import InsurancePage from './pages/InsurancePage';
import CompetitiveIntelPage from './pages/CompetitiveIntelPage';
import PaymentAnalyticsPage from './pages/PaymentAnalyticsPage';
import SecurityUpdatesPage from './pages/SecurityUpdatesPage';
import SecurityEnginePage from './pages/SecurityEnginePage';
import ClinicalDocumentationPage from './pages/ClinicalDocumentationPage';
import CompliancePosturePage from './pages/CompliancePosturePage';
import PEMDashboardPage from './pages/PEMDashboardPage';
import LogisticsDashboardPage from './pages/LogisticsDashboardPage';
import ClinicTabPage from './pages/ClinicTabPage';
import ClinicalOversightPage from './pages/ClinicalOversightPage';
import ProtocolLibraryPage from './pages/ProtocolLibraryPage';
import PhysicianDashboardPage from './pages/PhysicianDashboardPage';
import AlliancePartnersPage from './pages/AlliancePartnersPage';
import PermissionManagerPage from './pages/PermissionManagerPage';
import UserManagementPage from './pages/UserManagementPage';
import RoleManagementPage from './pages/RoleManagementPage';
import SidebarVisibilityPage from './pages/SidebarVisibilityPage';
import FeedbackTriagePage from './pages/FeedbackTriagePage';
import AdminAuditPage from './pages/AdminAuditPage';
import CompliancePage from './pages/CompliancePage';
import IntelligencePage from './pages/IntelligencePage';
import CRMImportPage from './pages/CRMImportPage';
import InventoryPage from './pages/InventoryPage';
import PemAssignmentPage from './pages/PemAssignmentPage';
import CalendarOversightPage from './pages/CalendarOversightPage';
import MobileManagementPage from './pages/MobileManagementPage';
import IdleWarningModal from './components/IdleWarningModal';
import FirstLoginBanner from './components/FirstLoginBanner';
import TrainingWarningBanner from './components/TrainingWarningBanner';
import { NavHistoryContext, useNavHistoryController, NavBackButton } from './lib/NavHistory';
import { readParam, writeParams, ALLOWED_KEYS } from './lib/urlState';
import { clearBrowserBackMarker, isHoldingBrowserStep } from './lib/browserBackStep';
import { mintLoginNonce, clearLoginNonce, entryIsOurs } from './lib/loginNonce';
import ReminderBanner from './components/ReminderBanner';
import { NavNonceProvider } from './lib/navNonce';
import ToastHost from './components/ToastHost';
import './App.css';

// ── Patient Portal Direct View (bypasses internal login, uses session from LoginPage) ──
function PatientPortalViewDirect({ portalSession, onLogout }) {
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadData() {
      try {
        // C2 (audit Apr 26 2026): use opaque sessionToken (was: integer tokenId)
        const data = await window.zignagenixAPI.portalGetData(portalSession.sessionToken);
        if (data?.error) {
          setError(data.message || 'Failed to load your records.');
        } else {
          setPortalData(data);
        }
      } catch (err) {
        setError('Unable to load records. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [portalSession]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
        <p style={{ color: '#6C757D', fontWeight: 600 }}>Loading your health records securely...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ padding: '2rem', maxWidth: '400px', textAlign: 'center', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h2 style={{ margin: '0 0 0.75rem', color: '#111827' }}>Session Error</h2>
          <p style={{ color: '#6C757D', marginBottom: '1.5rem' }}>{error}</p>
          <button onClick={onLogout} style={{
            padding: '0.5rem 1.5rem', background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
          }}>Back to Login</button>
        </div>
      </div>
    );
  }

  // Render the full PatientPortalView, passing the session so it skips its own login
  return <PatientPortalView onNavigate={onLogout} portalSessionProp={portalSession} portalDataProp={portalData} />;
}

// ── Error boundary to catch page rendering crashes ──────────────────────────
class PageErrorBoundary extends Component {
  state = { error: null, errorInfo: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(err, info) {
    this.setState({ errorInfo: info });
    console.error('[PageErrorBoundary] Page:', this.props.pageName || 'unknown');
    console.error('[PageErrorBoundary] Error:', err);
    console.error('[PageErrorBoundary] Component Stack:', info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, color: '#d32f2f', fontFamily: 'monospace' }}>
          <h2 style={{ marginBottom: 12 }}>Page Render Error</h2>
          <p style={{ marginBottom: 8, fontSize: 14, color: '#1a1a1a' }}>
            {'Page: '}{this.props.pageName || 'unknown'}
          </p>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, background: '#fff3f3', padding: 16, borderRadius: 8 }}>
            {this.state.error?.toString()}
            {'\n\n'}
            {this.state.error?.stack}
            {this.state.errorInfo?.componentStack ? '\n\nComponent Stack:\n' + this.state.errorInfo.componentStack : ''}
          </pre>
          <button onClick={() => this.setState({ error: null, errorInfo: null })}
            style={{ marginTop: 16, padding: '8px 20px', background: '#2E8B98', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const pages = {
  dashboard: Dashboard,
  patients: Patients,
  encounters: Encounters,
  labs: Labs,
  medications: Medications,
  vitals: Vitals,
  allergies: Allergies,
  // Unified Messages surface — Phase 1 of the Secure Chat + Secure Email
  // consolidation. 'chat' and 'email' below are kept as legacy route IDs
  // so existing bookmarks open the new surface on the matching tab (same
  // pattern as Activity inbox + research/researchdb → BiolabPage).
  messages: MessagesPage,
  chat:  (props) => <MessagesPage {...props} initialTab="chat" />,
  email: (props) => <MessagesPage {...props} initialTab="email" />,
  airesearch: AIResearchPage,
  bi: BIDashboard,
  // Unified Revenue hub — Phase 1 of the Operations-section revenue
  // consolidation. The five legacy revenue-cycle routes (billing,
  // estimates, stripe, stripereports, paymentanalytics) all redirect
  // here with the matching initialTab so existing bookmarks keep working.
  // Insurance stays standalone — it has its own internal tabs and is
  // about eligibility, not money flow.
  revenue: RevenueHubPage,
  billing: (props) => <RevenueHubPage {...props} initialTab="invoices" />,
  scheduling: SchedulingPage,
  crm: CRMPage,
  'my-day': CRMPage,
  adminai: AdminAIPage,
  // Unified Activity inbox — Phase 1 of the Notifications + AI Alerts + Tasks
  // consolidation. 'notifications' and 'aialerts' below are kept as legacy
  // route IDs so existing bookmarks open the new surface on the matching tab
  // (same pattern we used for research/researchdb → BiolabPage above).
  activity: ActivityPage,
  notifications: (props) => <ActivityPage {...props} initialTab="notifications" />,
  dataexport: DataExportPage,
  portal: PatientPortalPage,
  patientportal: PatientPortalView,
  totp: TOTPSetupPage,
  audit: AuditLog,
  // Card P244 — the triage side of the in-CRM 'Report a problem' button.
  feedbacktriage: FeedbackTriagePage,
  settings: SettingsPage,
  awscloud: AWSCloudPage,
  gohighlevel: GoHighLevelPage,
  stripe:        (props) => <RevenueHubPage {...props} initialTab="stripe" />,
  stripereports: (props) => <RevenueHubPage {...props} initialTab="reports" />,
  // Legacy routes — Research Studies + Research Database merged into
  // BiolabPage (now "Research Repository"). Keeping the route IDs alive
  // so existing bookmarks redirect cleanly to the new consolidated page.
  research: BiolabPage,
  researchdb: BiolabPage,
  estimates: (props) => <RevenueHubPage {...props} initialTab="estimates" />,
  biolab: BiolabPage,
  reports: ReportBuilderPage,
  'ceo-dashboard': CeoDashboardPage,
  // Unified Personnel hub — Phase 1 of the HR consolidation. Legacy route
  // IDs (personnel, leave, personnelactions) all redirect to the hub with
  // the matching initialTab so existing bookmarks land where expected.
  personnel: PersonnelHubPage,
  leave: (props) => <PersonnelHubPage {...props} initialTab="leave" />,
  personnelactions: (props) => <PersonnelHubPage {...props} initialTab="actions" />,
  aialerts: (props) => <ActivityPage {...props} initialTab="alerts" />,
  // Unified Telehealth hub — Phase 1 of the Telehealth + Telemedicine
  // consolidation. The two surfaces are sequential phases of the same
  // patient encounter (schedule → call) so the sidebar now has one
  // entry "Telehealth" with two tabs. Old route IDs preserved as
  // redirects so existing bookmarks keep working.
  telehealth: TelehealthHubPage,
  telemedicine: (props) => <TelehealthHubPage {...props} initialTab="schedule" />,
  insurance: InsurancePage,
  competitiveintel: CompetitiveIntelPage,
  paymentanalytics: (props) => <RevenueHubPage {...props} initialTab="analytics" />,
  securityupdates: SecurityUpdatesPage,
  securityengine: SecurityEnginePage,
  complianceposture: CompliancePosturePage,
  pemdashboard: PEMDashboardPage,
  logisticsdashboard: LogisticsDashboardPage,
  clinictab: ClinicTabPage,
  clinicaloversight: ClinicalOversightPage,
  protocollibrary: ProtocolLibraryPage,
  physiciandashboard: PhysicianDashboardPage,
  alliancepartners: AlliancePartnersPage,
  clinicaldocs: ClinicalDocumentationPage,
  // Aliases per Daniel's spec section labels
  clinicaldocumentation: ClinicalDocumentationPage,
  patientencounters: ClinicalDocumentationPage,
  progressnotes: ClinicalDocumentationPage,
  soapnotes: ClinicalDocumentationPage,
  hpnotes: ClinicalDocumentationPage,
  dischargesummary: ClinicalDocumentationPage,
  procedurenotes: ClinicalDocumentationPage,
  // Unified Access Control hub — Phase 1 of the RBAC admin consolidation.
  // Four legacy routes (usermanagement, rolemanagement, permissions,
  // sidebarvisibility) all redirect to the hub with the matching initialTab.
  access: AccessControlHubPage,
  usermanagement:    (props) => <AccessControlHubPage {...props} initialTab="users" />,
  rolemanagement:    (props) => <AccessControlHubPage {...props} initialTab="roles" />,
  sidebarvisibility: (props) => <AccessControlHubPage {...props} initialTab="visibility" />,
  adminaudit: AdminAuditPage,
  compliance: CompliancePage,
  intelligence: IntelligencePage,
  permissions: (props) => <AccessControlHubPage {...props} initialTab="permissions" />,
  crmimport: CRMImportPage,
  mobilemanagement: MobileManagementPage,
  inventory: InventoryPage,
  pemAssignment: PemAssignmentPage,
  calendarOversight: CalendarOversightPage,
};

// Task #60 (Jun 8 2026): role-aware default landing page. When a user
// signs in (or their session is restored on mount), route them to the
// surface that matches their day-one workflow instead of the generic
// my-day page. We gate the recommended landing on the corresponding
// permission so a misconfigured role doesn't get bounced to a page
// that immediately renders an access-denied state.

/**
 * ⛔⛔ WHAT SIGN-OUT MUST LEAVE BEHIND FOR THE NEXT PERSON: NOTHING OF THIS ONE.
 *
 * Review round 3 (Cowork, PR #620, 11 Oct 2026). Clearing the address bar is
 * not enough on a shared workstation — the CRM keeps working state in browser
 * storage too, and one of those keys is NOT SCOPED TO A USER:
 *
 *   `zg.crmsearch.v1.<resource>`  — the CRM free-text search. It carries no
 *      user id, so the next person to sign in on this machine reads the
 *      previous person's term. src/components/CrmFilterBar/CrmFilterBar.js
 *      states exactly why that matters: "a CRM search is routinely a
 *      patient/lead NAME". MEASURED IN CHROME: it survived both a sign-out and
 *      an idle timeout.
 *
 * ⚠ The per-user keys (`crm.workspace.tabs.v1.<userId>`,
 * `zg.crm.pipeline.v1.<userId>`, `zg.crm.pipelineplace.v1.<userId>.<id>`) are
 * RESIDUE, not a leak — the next person reads their own key and sees nothing
 * of their predecessor's — and they are the memory that makes a returning user
 * land where they left off. They are deliberately NOT cleared here. Removing
 * them would quietly delete the feature this card shipped; what has to go is
 * the key that crosses users.
 *
 * ⚠ Everything is wrapped: storage throws outright in a private window and
 * when site data is blocked, and a sign-out must complete regardless.
 */
/** Monotonic within this document — see the sessionNonce comment below. */
let _sessionSeq = 0;
function nextSessionSeq() { _sessionSeq += 1; return `${_sessionSeq}.${Date.now()}`; }

function clearPlaceStorage() {
  try {
    const doomed = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith('zg.crmsearch.')) doomed.push(k);
    }
    for (const k of doomed) window.sessionStorage.removeItem(k);
  } catch { /* storage unavailable — nothing to clear */ }
}

function defaultLandingForRole(user) {
  const role = user?.role;
  const perms = new Set(user?.permissions || []);
  if (role === 'patientportal') return 'dashboard';
  if (role === 'patient_experience' && perms.has('pem:read')) return 'pemdashboard';
  // Chief Medical Director lands on the cross-patient oversight view.
  if (role === 'chief_medical_director' && perms.has('clinical_oversight:read')) return 'clinicaloversight';
  // Treating / consulting physicians land on the per-patient Clinic Tab.
  if ((role === 'treating_physician' || role === 'consulting_physician') && perms.has('physician:read')) return 'clinictab';
  if ((role === 'physician' || role === 'nurse') && perms.has('physician:read')) {
    return 'physiciandashboard';
  }
  if ((role === 'sysadmin' || role === 'admin' || role === 'manager') && perms.has('users:read')) {
    return 'access';
  }
  return 'my-day';
}

// Task #81 (Jun 10 2026): patient-facing magic-link proposal page lives
// OUTSIDE the authenticated app shell. Detect /proposal/<token> at the
// pathname level BEFORE the auth check runs, render the public surface,
// and short-circuit the rest of App() entirely. No sidebar, no idle
// timer, no auth — just the patient view consuming the public Express
// endpoints from Phase 2b (routes/treatment_proposals_public.js).
function getPublicProposalToken() {
  try {
    const p = (typeof window !== "undefined" && window.location && window.location.pathname) || "";
    const m = p.match(/^\/proposal\/([A-Za-z0-9_-]+)\/?$/);
    return m ? m[1] : null;
  } catch (_) { return null; }
}

// Phase 5i (Jun 11 2026): lead intake magic-link upload page — same
// outside-the-shell pattern as the proposal page above.
function getLeadIntakeToken() {
  try {
    const p = (typeof window !== "undefined" && window.location && window.location.pathname) || "";
    const m = p.match(/^\/intake\/([A-Za-z0-9_-]+)\/?$/);
    return m ? m[1] : null;
  } catch (_) { return null; }
}

function App() {
  // Public proposal page short-circuit — runs before any auth state
  // is established, before any IPC call, before session check.
  const publicToken = getPublicProposalToken();
  if (publicToken) {
    return <PublicProposalPage token={publicToken} />;
  }
  const intakeToken = getLeadIntakeToken();
  if (intakeToken) {
    return <LeadIntakeUploadPage token={intakeToken} />;
  }
  // Luisa R1 (Jun 11 2026): public bilingual patient education guide.
  // Non-PHI, no token — outside the auth shell like the pages above.
  try {
    if ((window.location.pathname || "").match(/^\/education\/?$/)) {
      return <LeadEducationGuidePage />;
    }
  } catch (_) { /* ignore */ }

  return <AuthenticatedApp />;
}

// Authenticated application root (#67). Everything below App()'s public-route
// short-circuits lives here, so these hooks NEVER run on public (proposal /
// intake / education) loads — preserving prior behavior (checkSession /
// onSessionTimeout / reportActivity fire only on non-public loads, exactly as
// before) while satisfying react-hooks/rules-of-hooks (App() is now hook-free).
// This is also the mount point for the future #65 NavHistoryProvider: its
// hooks belong at the top here, above the portalSession/showRecovery/!user/
// securitySetup early returns further down.
function AuthenticatedApp() {
  const [user, setUser] = useState(null);
  const prevUidRef = React.useRef(null);
  const sessionNonceRef = React.useRef('');
  const [activePage, setActivePage] = useState('my-day');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [securitySetup, setSecuritySetup] = useState(null);
  const [trainingWarning, setTrainingWarning] = useState(null); // Task #174 warn-then-block
  // Task #204 — mobile drawer state. Always false on desktop (the CSS just
  // ignores it via the @media-min-width rules); true/false drives the
  // hamburger-driven slide-in on phones/tablets ≤768px.
  const [navOpen, setNavOpen] = useState(false);
  // securitySetup = { requiresPasswordChange, requiresSecuritySetup }
  const [portalSession, setPortalSession] = useState(null); // Patient portal direct login
  /*
   * ⛔⛔ THREE STATES, NOT TWO — Card PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026.
   *
   * `user === null` meant BOTH "signed out" AND "we have not looked yet", and
   * the URL mirror below treated the second as the first. That is what broke
   * Card PARTNER-LIST-REFRESH's reload restore — see the comment there.
   * This flag separates them: false = BLIND, and a blind state must not act.
   */
  /*
   * ⚠ THE NAV CONTROLLER IS CREATED FURTHER DOWN (it needs `activePage`), and
   * the popstate handler above closes over this ref rather than the value, so
   * it always consults the LIVE dirty-form guard rather than one captured at
   * mount. Defaults to "yes, you may leave" so the handler is safe during the
   * first render, before the controller exists.
   */
  const navRef = React.useRef({ mayLeave: () => true });

  const [sessionChecked, setSessionChecked] = useState(false);

  /*
   * ⛔⛔ A BROWSER-BACK MARKER BELONGS TO ONE SIGNED-IN SESSION — review round 4.
   *
   * `history.state` outlives a sign-out (src/lib/urlState.js carries it through
   * every replaceState on purpose), so a marker minted by one user was still
   * sitting on the entry when the next user signed in IN THE SAME DOCUMENT, and
   * their first CRMPage mount adopted it. MEASURED in Chrome and WebKit.
   *
   * ⚠ DERIVED DURING RENDER, not in an effect. An effect would mint the nonce
   * one render too late: CRMPage mounts in the SAME commit that sets `user`, so
   * its hook would arm with an empty nonce, fail to match it a moment later and
   * push a second entry. The ref-adjustment shape below is synchronous and
   * correct on the very first render in which a user exists.
   *
   * ⚠ It is NOT the user id alone. The same person signing out and back in must
   * get a different nonce, or their own stale marker is adoptable; the counter
   * guarantees that.
   */
  const uid = user ? String(user.id) : null;
  if (uid !== prevUidRef.current) {
    prevUidRef.current = uid;
    sessionNonceRef.current = uid ? `${uid}.${nextSessionSeq()}` : '';
  }
  const sessionNonce = sessionNonceRef.current;

  // Check for existing session on mount
  useEffect(() => {
    async function checkSession() {
      try {
        const sess = await window.zignagenixAPI.getSession();
        if (sess.authenticated) {
          /*
           * ⛔ KEEP an existing nonce. This is the RELOAD path: the entry we
           * are about to restore from was stamped by this same login, and
           * minting a new value here would disown it and throw the person back
           * to their role default — the very defect round 3 fixed.
           */
          mintLoginNonce({ keepExisting: true });
          setUser(sess.user);
          // Push permissions into the client-side API gate
          window.zignagenixAPI.setUserPermissions?.(sess.user?.permissions || []);
          /*
           * ⛔⛔ A RELOAD IS NOT A SIGN-IN — Card PARTNER-LIST-REFRESH.
           *
           * Task #60's role-appropriate landing is right for a FRESH sign-in
           * and wrong for a refresh. Measured in the 8 Oct demo: a staff
           * member on the phone with a new consultation pressed reload to see
           * a record that had just been created, and was thrown out of the
           * contact they were on and onto My Day. REQUIREMENTS.md documents
           * the My Day landing, so this read as intended behaviour rather
           * than as a bug, which is why it survived.
           *
           * `?page=` is written by the effect below on every navigation, so
           * its presence means "this browser was already somewhere" — a
           * reload, a session restore inside the idle window, or a deep link
           * a colleague pasted. Absent, there is no place to return to and
           * the role default is still correct.
           *
           * ⚠ VALIDATED AGAINST THE PAGE MAP, not trusted. urlState only ever
           * yields a slug-shaped value, and an unknown slug (a stale bookmark,
           * a hand-edited address, a page removed in a later release) falls
           * through to the role default rather than rendering a blank shell.
           */
          /*
           * ⛔⛔ AND ONLY IF THIS ENTRY IS OURS — review round 5.
           *
           * A cold load restores from the URL. If this tab's history entry was
           * stamped by a DIFFERENT login, everything on it belongs to whoever
           * was signed in before, and adopting any of it is the cross-user
           * leak. Scrub the entry to this person's own landing page and carry
           * on. An UNOWNED entry is not a stranger's — it is a pasted link or
           * a bookmark, and those keep working (see loginNonce.entryIsOurs).
           */
          let ours = true;
          try { ours = entryIsOurs(window.history.state); } catch { ours = true; }
          const wanted = ours ? readParam('page') : '';
          const landing = wanted && pages[wanted] ? wanted : defaultLandingForRole(sess.user);
          if (!ours) {
            writeParams(Object.fromEntries(ALLOWED_KEYS.map((k) => [k, k === 'page' ? landing : ''])));
            clearBrowserBackMarker();
          }
          setActivePage(landing);
        }
      } catch (err) {
        console.error('Session check failed:', err);
      } finally {
        // ⚠ `finally`, so a THROWN session check still ends the blind state.
        // Leaving it blind forever would mean the keys are never cleared on a
        // shared workstation — a failure that hides rather than shows.
        setSessionChecked(true);
      }
    }
    checkSession();
  }, []);

  /*
   * ⛔⛔ THE ADDRESS BAR AND THE SCREEN MUST NOT DISAGREE — review round 4.
   *
   * src/lib/browserBackStep.js pushes history entries and deliberately never
   * traverses, so a Back press lands on an entry THIS APP WROTE — but nothing
   * was listening for it at the top level. MEASURED in Chrome and WebKit:
   * after leaving the CRM and pressing Back the URL read `?page=crm` while the
   * screen still showed Settings, and a reload then went somewhere else again.
   *
   * ⚠ TWO JOBS, BY SESSION STATE:
   *   SIGNED IN   adopt the URL's page when it names a real one (the person
   *               asked to go back), or rewrite the URL to match the screen
   *               when it does not — either way they end up agreeing.
   *   SIGNED OUT  scrub. The entry underneath one user's session can still be
   *               carrying their place, and nobody is going to render it — so
   *               the keys and the marker come off whatever we land on.
   *
   * ⚠ BLIND DOES NOTHING. While the session check is still in flight this is
   * inert, for the same reason the URL mirror below is.
   */
  useEffect(() => {
    if (!sessionChecked) return undefined;
    const onPop = () => {
      if (!user) {
        writeParams(Object.fromEntries(ALLOWED_KEYS.map((k) => [k, ''])));
        clearBrowserBackMarker();
        return;
      }

      /*
       * ⛔⛔ (1) WHOSE ENTRY IS THIS? — review round 5, the blocker.
       *
       * Sign-out can only rewrite the entry it is standing on. Every OTHER
       * entry in this tab — including the FORWARD ones a Back press leaves
       * behind — still carries the previous login's place. MEASURED in Chrome
       * and WebKit: A opened a contact, pressed Back (leaving a forward entry
       * holding it), signed out; B signed in in the same document, pressed
       * Forward, and A's contact opened in B's workspace and was persisted to
       * B's localStorage. An entry stamped by another login is scrubbed to
       * this person's current page and nothing on it is adopted.
       */
      let ours = true;
      try { ours = entryIsOurs(window.history.state); } catch { ours = true; }
      if (!ours) {
        writeParams(Object.fromEntries(ALLOWED_KEYS.map((k) => [k, k === 'page' ? activePage : ''])));
        clearBrowserBackMarker();
        return;
      }

      const wanted = readParam('page');

      /*
       * ⛔⛔ (2) UNSAVED WORK — review round 5, major item 2.
       *
       * This traversal is same-document, so the `beforeunload` that used to
       * catch a half-written note never fires. Adopting a different page here
       * unmounts whatever is holding that note. Ask the same question every
       * other navigation control asks, and if the person says no, put the
       * address bar back where the screen already is.
       */
      if (wanted && pages[wanted] && wanted !== activePage && !navRef.current.mayLeave()) {
        writeParams({ page: activePage });
        return;
      }

      if (wanted && pages[wanted]) {
        if (wanted !== activePage) setActivePage(wanted);
      } else {
        writeParams({ page: activePage });
      }

      /*
       * ⛔ (3) A CONTACT THE SCREEN HAS NOT OPENED — review round 5, item 3.
       *
       * Open a record, press Back (it closes), press FORWARD: the entry that
       * held it still says `contact=`, nothing is open, and a reload reopened
       * a record the person had closed. A hook HOLDING an entry means the
       * screen really has one open; no hold means this is a leftover.
       */
      if (readParam('contact') && !isHoldingBrowserStep()) {
        writeParams({ contact: '', ctab: '' });
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [sessionChecked, user, activePage]);

  // Listen for session timeout
  useEffect(() => {
    if (window.zignagenixAPI?.onSessionTimeout) {
      window.zignagenixAPI.onSessionTimeout((reason) => {
        setUser(null);
        setSecuritySetup(null);
        // P130: the reason comes from apiClient, which is the only layer that
        // knows when this tab last saw a human. Defaulting to 'idle_timeout'
        // is what told Daniel his session had "expired" when it had been
        // ended somewhere else — so an UNKNOWN reason now says it is unknown
        // rather than picking the comfortable answer.
        setSessionExpired(typeof reason === 'string' && reason ? reason : 'ended_unknown');
        setActivePage('dashboard');
        // Clear permission gate so no API calls can pass after timeout
        window.zignagenixAPI.clearUserPermissions?.();
        // SECURITY (HIPAA §164.312(a)(2)(iii)): defence-in-depth — apiClient
        // already invalidates the server session and wipes sessionStorage in
        // _forceServerLogoutOnTimeout, but clear here too so any race between
        // the callback and the apiClient cleanup can't leave a usable token.
        try {
          sessionStorage.removeItem('zignagenix_session');
          sessionStorage.removeItem('zignagenix_tab_alive');
        } catch { /* sessionStorage may be unavailable in some browser modes */ }
      });
    }
  }, []);

  // P110 (c): another tab signed out, timed out, or took the session over.
  // apiClient has already wiped this tab's local state by the time this runs —
  // all that is left is to drop the authenticated tree (which clears PHI from
  // the screen) and say WHY on the sign-in page, rather than letting the user
  // discover it as a silent 401 on their next click.
  useEffect(() => {
    window.zignagenixAPI?.onPeerSignout?.((reason) => {
      setUser(null);
      setSecuritySetup(null);
      setActivePage('dashboard');
      setSessionExpired(reason === 'takeover' ? 'displaced' : (reason || true));
      window.zignagenixAPI.clearUserPermissions?.();
    });
  }, []);

  // Report user activity for session timeout reset (debounced to 5s).
  // Sets a flag in apiClient so the next heartbeat tick will extend the
  // server session — keeping the session alive only while the user is active.
  useEffect(() => {
    let timer = null;
    function reportActivity() {
      if (timer) return;
      timer = setTimeout(() => { timer = null; }, 5000);
      if (user && window.zignagenixAPI?.reportActivity) {
        window.zignagenixAPI.reportActivity();
      }
    }
    // Genuine human-interaction events only (Task #79 PR-1). 'input' covers
    // typing into fields (keydown misses IME/paste); the whole handler is
    // throttled to 5s above, which also bounds mousemove/scroll cost.
    const events = ['mousemove', 'mousedown', 'keydown', 'input', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, reportActivity, { passive: true }));
    // Returning to a hidden tab is activity too (visibilitychange → visible).
    function onVisible() {
      if (document.visibilityState === 'visible') reportActivity();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      events.forEach(e => window.removeEventListener(e, reportActivity));
      document.removeEventListener('visibilitychange', onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [user]);

  const handleLogin = useCallback((userData, loginResult) => {
    /*
     * ⛔⛔ A NEW LOGIN GETS A NEW NONCE, so nothing the previous person left in
     * this tab's history reads as theirs. `keepExisting: false` is the whole
     * point — see src/lib/loginNonce.js.
     */
    mintLoginNonce({ keepExisting: false });
    setUser(userData);
    // Push effective permissions into the client-side API gate
    window.zignagenixAPI.setUserPermissions?.(userData?.permissions || []);
    setSessionExpired(false);
    setShowRecovery(false);
    // Task #174: warn-then-block overdue-training banner (dismissible per session).
    setTrainingWarning(loginResult?.trainingWarning || null);

    // Check if user needs to complete security setup
    const needsPwChange = loginResult?.requiresPasswordChange;
    const needsSQ = loginResult?.requiresSecuritySetup;
    const needsTOTPSetup = loginResult?.requiresTOTPSetup;

    if (needsPwChange || needsSQ || needsTOTPSetup) {
      setSecuritySetup({
        requiresPasswordChange: needsPwChange,
        requiresSecuritySetup: needsSQ,
        requiresTOTPSetup: needsTOTPSetup,
      });
    } else {
      setSecuritySetup(null);
      // Task #60: role-aware landing on fresh login (not just session restore).
      setActivePage(defaultLandingForRole(userData));
    }
  }, []);

  // P110: `reason` distinguishes an automatic logoff from a deliberate one so
  // the sign-in page can say WHICH happened. ⚠ This is also the Sidebar's
  // onClick handler, which passes a SyntheticEvent — hence the typeof check
  // rather than trusting the first argument.
  const handleLogout = useCallback(async (reason) => {
    const why = typeof reason === 'string' ? reason : 'signout';
    // (c) Tell the other tabs BEFORE the server call: they share this session
    // via the httpOnly cookie, and without this they would discover it only as
    // a bare 401 on their next request.
    window.zignagenixAPI.broadcastSignout?.(why);
    try {
      await window.zignagenixAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    setUser(null);
    setSecuritySetup(null);
    setActivePage('dashboard');
    setSessionExpired(why === 'signout' ? false : why);
    // Wipe permission gate on logout (logout() also calls this, belt-and-suspenders)
    window.zignagenixAPI.clearUserPermissions?.();
  }, []);

  const handleSecuritySetupComplete = useCallback(() => {
    setSecuritySetup(null);
    setActivePage('my-day');
  }, []);

  // Patient portal login from the login page (no staff session needed)
  const handlePortalLogin = useCallback((portalResult) => {
    setPortalSession(portalResult);
  }, []);

  const handlePortalLogout = useCallback(() => {
    setPortalSession(null);
  }, []);

  // Route-level nav history (#65 Phase 1). Top-level hook, above all early
  // returns. `nav.navigate` is wired to the authenticated shell's onNavigate
  // (single push choke point); the RAW setActivePage above (landing / auth
  // transitions) intentionally bypasses it so it creates no history entry.
  const nav = useNavHistoryController(activePage, setActivePage, user?.id ?? null);
  // Keep the popstate handler's view of the guard current — see navRef above.
  navRef.current = nav;

  /*
   * Mirror the current page into `?page=` so a reload can come back to it.
   *
   * ⚠ Only while SIGNED IN. Writing it on the sign-in screen would leave the
   * last page of the previous session in the address bar of a shared
   * workstation, which is a disclosure for no benefit — the next sign-in gets
   * the role default by design. Signing out therefore CLEARS every key this
   * module owns rather than leaving them to rot in the URL.
   *
   * ⛔ The page id only. The open contact, the open partner org and the open
   * tab are written by the screens that own them (CRMPage, ContactDetailView,
   * AlliancePartnersPage) because only those screens know when their selection
   * is real — and all of them go through the same allowlist, so a name cannot
   * reach the address bar from any of them.
   */
  useEffect(() => {
    /*
     * ⛔⛔ DO NOTHING WHILE THE SESSION IS UNKNOWN — Card
     * PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026.
     *
     * ⚠⚠ MEASURED IN A REAL BROWSER, and it is why Card PARTNER-LIST-REFRESH's
     * reload restore never actually worked in one. Loading
     * `?page=crm&tab=pipeline&pipeline=1&stage=1` traced as:
     *
     *      11ms  initial            ?page=crm&tab=pipeline&pipeline=1&stage=1
     *      59ms  afterReplaceState  (empty)      <-- THIS effect, user still null
     *      64ms  afterReplaceState  ?page=access <-- the role default
     *
     * `user` is `useState(null)` and `checkSession()` is async, so on EVERY
     * cold load this effect ran first, read "no user" as "signed out", and
     * wiped every key — including the `?page=` that `checkSession` was about
     * to read. The restore then found an empty URL and fell through to
     * defaultLandingForRole. The staff member was thrown out of the record
     * they were on, which is the exact defect that card set out to fix.
     *
     * ⚠ NO EXISTING TEST COULD SEE IT: tests/react/placeRestoredOnReload.test.js
     * and contactPlaceAndDraft.test.js render the CHILD with the URL already
     * set, so they never exercise this ordering, and CrmUrlPlaceWiring is a
     * static gate. tests/react/appReloadKeepsPlace.test.js now drives App.js
     * itself with a session check that resolves on a later tick.
     */
    if (!sessionChecked) return;
    if (!user) {
      /*
       * ⚠ DERIVED FROM THE ALLOWLIST, NOT RETYPED — Card
       * PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026.
       *
       * This was a hand-written list of the five key names. That is a second
       * copy of the allowlist, and the moment this card added `pipeline` and
       * `stage` the copy was wrong: sign-out would have cleared five keys and
       * left two behind in the address bar, with nothing to notice it. Reading
       * ALLOWED_KEYS means a key added to urlState is cleared on sign-out by
       * construction. tests/react/urlState.test.js pins the pairing.
       */
      writeParams(Object.fromEntries(ALLOWED_KEYS.map((k) => [k, ''])));
      clearPlaceStorage();
      // ⛔ And the browser-back marker, which lives in history.state rather
      // than the query string and so survives the clear above untouched.
      clearBrowserBackMarker();
      // ⛔⛔ And the login's ownership of every OTHER entry in this tab's
      // history — the ones a sign-out cannot reach, which is round 5's blocker.
      clearLoginNonce();
      return;
    }
    writeParams({ page: activePage });
  }, [sessionChecked, user, activePage]);

  // Show patient portal view (logged in via portal access code)
  if (portalSession) {
    return (
      <PageErrorBoundary pageName="PatientPortalView">
        <PatientPortalViewDirect
          portalSession={portalSession}
          onLogout={handlePortalLogout}
        />
      </PageErrorBoundary>
    );
  }

  // Show recovery page
  if (showRecovery) {
    return (
      <PageErrorBoundary pageName="RecoveryPage">
        <RecoveryPage onBack={() => setShowRecovery(false)} />
      </PageErrorBoundary>
    );
  }

  // Not logged in — show login page
  if (!user) {
    return (
      <PageErrorBoundary pageName="LoginPage">
        <LoginPage
          onLogin={handleLogin}
          onPortalLogin={handlePortalLogin}
          sessionExpired={sessionExpired}
          onRecovery={() => setShowRecovery(true)}
        />
      </PageErrorBoundary>
    );
  }

  // Logged in but needs security setup
  if (securitySetup) {
    return (
      <PageErrorBoundary pageName="SecuritySetupPage">
        <SecuritySetupPage
          user={user}
          requiresPasswordChange={securitySetup.requiresPasswordChange}
          requiresSecuritySetup={securitySetup.requiresSecuritySetup}
          requiresTOTPSetup={securitySetup.requiresTOTPSetup}
          onComplete={handleSecuritySetupComplete}
        />
      </PageErrorBoundary>
    );
  }

  const PageComponent = pages[activePage] || Dashboard;

  return (
    // #65 Phase 1: provide the nav controller to the authenticated shell so
    // the global Back button (and future Phase-2 useNavStep consumers) can read
    // it. Mounts only here — after the auth-gate early returns — so it never
    // wraps public routes (those return in App() before AuthenticatedApp).
    <NavHistoryContext.Provider value={nav}>
    <div className={`app-layout ${navOpen ? 'app-layout--nav-open' : ''}`}>
      {/* Mobile hamburger — visible only ≤768px via CSS. Sidebar is always
          rendered; on mobile its visibility is driven by the `navOpen` state. */}
      <button
        type="button"
        className="app-nav-toggle"
        aria-label={navOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={navOpen}
        aria-controls="app-sidebar"
        onClick={() => setNavOpen(v => !v)}
      >
        <span className="app-nav-toggle-icon" aria-hidden="true">
          {navOpen ? '✕' : '☰'}
        </span>
      </button>
      {/* Tap-anywhere backdrop to close the drawer on mobile. */}
      {navOpen && (
        <div
          className="app-nav-backdrop"
          role="presentation"
          onClick={() => setNavOpen(false)}
        />
      )}
      <Sidebar
        activePage={activePage}
        onNavigate={nav.navigate}
        user={user}
        onLogout={handleLogout}
        isOpen={navOpen}
        onClose={() => setNavOpen(false)}
      />
      <main className="app-main" data-page={activePage}>
        {/* #65: single global Back (top-left of content). Always rendered;
            DISABLED (greyed), not hidden, on a root/first view so it is never a
            dead-end. Same control feeds the keyboard shortcuts, the Capacitor
            hardware back, and the dirty-form guard (see src/lib/NavHistory.js). */}
        <NavBackButton />
        {/* Task #60 (Jun 8 2026): role-specific first-login orientation
            banner. Renders only when onboarding:status returns
            firstLoginCompleted=false. Lives outside PageErrorBoundary so
            a banner render bug never knocks out the page. */}
        <FirstLoginBanner user={user} />
        {/* Task #174: overdue-training warning banner (7-day grace before login
            is blocked). Dismissible for the session. */}
        <TrainingWarningBanner warning={trainingWarning} onDismiss={() => setTrainingWarning(null)} />
        {/* Phase 5e (Jun 11 2026): on-login follow-up banner for clinical
            staff. Self-gates on patient_reminders:read and renders nothing
            when the queue is empty. Outside PageErrorBoundary so a banner
            bug never knocks out the page. */}
        <ReminderBanner user={user} onNavigate={nav.navigate} />
        {/*
          * ⛔⛔ ONE NONCE FOR EVERY BROWSER-BACK STEP (Card P194).
          *
          * useBrowserBackStep adopts a history entry only when it can prove
          * ownership. Measured 31 Oct: 20 of 23 call sites passed no nonce, so
          * each pushed an entry per open that could never be reused — the
          * mechanism that closed the CEO drill-down modal (#766). Nine were
          * pages that already receive the prop below and never forwarded it;
          * eleven were drawers, panels and nested tabs with no props path at
          * all.
          *
          * ⚠ THE PROVIDER CARRIES THE SAME VALUE AS THE PROP, deliberately.
          * One namespace: a drawer and the page that owns it must be able to
          * adopt each other's entry, and two sources for one value is how they
          * come to disagree.
          */}
        {/*
          * ⛔ Card P219 item 8 — MOUNTED ONCE, ABOVE THE PAGE TREE AND OUTSIDE
          * every dialog. The notification has to outlive the dialog that
          * raised it: "you can keep working" is the requirement, and a toast
          * parented to the report dialog dies the moment Carlos closes it.
          */}
        <ToastHost />
        {/*
          * ⛔ Card P244 — MOUNTED ONCE, ABOVE THE PAGE TREE, for the same
          * reason ToastHost is: "every page, every logged-in user" is a
          * property of the SHELL, not something each page can be trusted to
          * remember. It sits inside the auth gate, so it never renders for a
          * signed-out visitor, and outside PageErrorBoundary — a page that has
          * crashed is exactly when somebody needs to report a problem, and a
          * launcher inside the boundary dies with the page it would report.
          */}
        <FeedbackLauncher activePage={activePage} />
        <NavNonceProvider value={sessionNonce}>
          <PageErrorBoundary key={activePage} pageName={activePage}>
            <PageComponent onNavigate={nav.navigate} user={user} activePage={activePage} sessionNonce={sessionNonce} />
          </PageErrorBoundary>
        </NavNonceProvider>
      </main>

      {/* Task #215 — idle-timeout warning. Mounts only while authenticated;
          unmounting cleans up the DOM listeners and pending timer. */}
      <IdleWarningModal onLogout={handleLogout} />

    </div>
    </NavHistoryContext.Provider>
  );
}

export default App;
